import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { useChatUI } from '../context/ChatUIContext';
import { useRefresh } from '../context/RefreshContext';
import { useNotificationSystem } from '../context/NotificationContext';
import { USER_TYPES } from '../constants/designer';
import NotificationBell from './NotificationBell';
import './MobileTabBar.css';

// ── MOBILE TAB BAR ───────────────────────────────────────────────────────────
// The header's actions, moved to the bottom on phones: feed, search, messages,
// notifications, profile.
//
// WHY THE BOTTOM. On a phone the top of the screen is the furthest point from
// a thumb, and it is where the browser puts its own chrome. Four icons crowded
// beside a logo also leave no room for the logo. This is the layout every
// mobile app converged on for the same reasons.
//
// PHONES ONLY. The desktop header has all the room it needs and a persistent
// bar there would just eat vertical space — see the media query in the CSS,
// which is also what hides the header copies of these buttons.

// The editor owns the whole screen, and a bar across the bottom would sit on
// top of the floor-plan toolbar. Same routes pull-to-refresh stays out of.
const HIDDEN_ON = ['/app/', '/try', '/share/'];

// ICON ONLY, no captions. Five translated captions do not fit a phone: at
// 390px each tab is 78px wide, and "Հաղորդագրություններ" needs three times
// that — every label came out as an ellipsis, which names nothing. The icons
// are the same ones from the header people already use, and `aria-label`
// carries the real name to screen readers and to a long-press tooltip.
function TabButton({ active, label, badge, onClick, children }) {
  return (
    <button
      type="button"
      className={`mtb-tab${active ? ' mtb-tab--on' : ''}`}
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-current={active ? 'page' : undefined}
    >
      <span className="mtb-icon">
        {children}
        {badge > 0 && <span className="mtb-badge">{badge > 9 ? '9+' : badge}</span>}
      </span>
    </button>
  );
}

export default function MobileTabBar() {
  const { user, profile } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { openMessages, close: closeMessages, messagesOpen } = useChatUI();
  const { unreadTotal = 0 } = useNotificationSystem();
  const refreshAll = useRefresh();

  // Signed out there is no feed, no messages and no profile — four of the five
  // tabs would be a login prompt wearing an icon.
  if (!user?.uid) return null;
  if (HIDDEN_ON.some(p => pathname.startsWith(p))) return null;

  const isDesigner = profile?.userType === USER_TYPES.DESIGNER;

  // THE OVERLAY WINS. Messages open on top of whatever page you were on, and
  // that page's route is still current — so Feed stayed lit while you were
  // reading a conversation and two tabs looked active at once. While the
  // overlay is up, the route tabs are not where you are.
  const at = (p) => pathname === p && !messagesOpen;

  // CLOSES THE OVERLAY ITSELF, rather than leaving it to the route change.
  //
  // ChatUIProvider closes on navigation, but navigating to the page you are
  // already on is not a navigation: opening Messages from the feed and then
  // tapping Feed produced no pathname change, no close, and a tab that
  // appeared dead. Closing here works from any tab, including that one.
  //
  // TAPPING THE TAB YOU ARE ALREADY ON RELOADS IT AND GOES TO THE TOP.
  // That is what the gesture means everywhere else — it is how you get back to
  // the newest posts without scrolling up through everything you have already
  // seen. `refreshAll` is the same routine pull-to-refresh runs, so the two
  // gestures cannot drift apart, and it forces past the feed's five-minute
  // cache rather than re-rendering what is already on screen.
  const go = (path) => {
    if (at(path)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      refreshAll();
      return;
    }
    closeMessages();
    navigate(path);
  };

  return (
    <nav className="mtb" aria-label={t('nav_primary')}>
      <TabButton active={at('/dashboard')} label={t('tab_feed')} onClick={() => go('/dashboard')}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9.5 12 3l9 6.5V20a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 20z" />
          <path d="M9.2 21.6v-7.2h5.6v7.2" />
        </svg>
      </TabButton>

      <TabButton active={at('/search')} label={t('tab_search')} onClick={() => go('/search')}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
          strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.6-3.6" />
        </svg>
      </TabButton>

      {/* Messages are an overlay, not a route, so there is no pathname to
          match — the provider reports whether it is open instead, which is
          what lights this tab while a conversation is on screen. */}
      <TabButton active={messagesOpen} label={t('tab_messages')} badge={unreadTotal} onClick={() => openMessages()}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.5 9.5 0 0 1-2.9-.4L4 21l1.4-3.9A8.2 8.2 0 0 1 3 11.5 8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z" />
        </svg>
      </TabButton>

      {/* The real bell, in tab clothing — same fetch, same mark-read-on-open,
          same badge.
          SHOWN TO EVERYONE. It was designers only, on the reasoning that
          liking is the only thing that produces a notification and only
          designers can be liked. Comment replies made that false: a customer
          who comments can be replied to, and on a phone this bar is the only
          place a notification could have appeared. */}
      <div className="mtb-tab mtb-tab--bell">
        <NotificationBell variant="tab" label={t('tab_alerts')} />
      </div>

      <TabButton active={at('/profile')} label={t('tab_profile')} onClick={() => go('/profile')}>
        {profile?.avatarUrl
          ? <img className="mtb-avatar" src={profile.avatarUrl} alt="" />
          : (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
            </svg>
          )}
      </TabButton>
    </nav>
  );
}
